import type {
  BakedBlockInstance,
  BlockDefinition,
  LeafPrimitive,
  TextValue,
  VariableMeta,
  Zone,
} from '@tsagko/label-contracts'

export type PropValues = Record<string, string>

/**
 * Matches a TextValue whose content is exactly a single `{name}` reference
 * (with optional surrounding whitespace). Mirrors the schema's accepted
 * shape — anything else passes through verbatim.
 */
const SINGLE_VARIABLE_RE = /^\s*\{([a-zA-Z][a-zA-Z0-9_]*)\}\s*$/

/**
 * Hybrid-A seam (planned removal under Option B): walks a block's layout and
 * rewrites every `{ content: '{name}' }` TextValue into a literal resolved
 * against `values[name]` (user-supplied at drop time), falling back to
 * `variables[name].default` if no value was provided. Non-`{name}` content
 * passes through unchanged.
 *
 * When Option B lands (render-time variable scoping in the pipeline), this
 * helper deletes and `bakeBlock` copies `block.layout` into `expansion`
 * verbatim, with values living in `BakedBlockInstance.props` and resolved at
 * render time via `resolveContent`. Until that slice ships, this is the
 * one place where bake-time substitution lives.
 */
export function applyVariablesToLayout(
  layout: Zone,
  variables: Record<string, VariableMeta>,
  values: PropValues
): Zone {
  if (layout.component && layout.component.type !== 'baked-block-instance') {
    return { ...layout, component: rewritePrimitive(layout.component, variables, values) }
  }
  if (layout.children && layout.split) {
    return {
      ...layout,
      children: [
        applyVariablesToLayout(layout.children[0], variables, values),
        applyVariablesToLayout(layout.children[1], variables, values),
      ],
    }
  }
  return layout
}

function resolveTextValue(
  tv: TextValue,
  variables: Record<string, VariableMeta>,
  values: PropValues
): TextValue {
  const match = SINGLE_VARIABLE_RE.exec(tv.content)
  if (!match) return tv
  const name = match[1]
  if (name === undefined) return tv
  // Key PRESENT (even with '') = explicit value. Key ABSENT = use default.
  // This allows operators to express "print empty" by clearing a field,
  // distinct from "use the declared default" (key absent in props).
  if (Object.hasOwn(values, name)) {
    return { content: values[name]! }
  }
  const fallback = variables[name]?.default
  if (fallback !== undefined && fallback !== '') return { content: fallback }
  // Leave the {name} placeholder intact — the render path renders the literal
  // brace text, which surfaces missing-default authoring mistakes visibly.
  return tv
}

function rewritePrimitive(
  p: LeafPrimitive,
  variables: Record<string, VariableMeta>,
  values: PropValues
): LeafPrimitive {
  if (p.type === 'text') {
    return { ...p, value: resolveTextValue(p.value, variables, values) }
  }
  if (p.type === 'localized-text' || p.type === 'localized-rich-text') {
    return {
      ...p,
      variants: p.variants.map(v => ({
        ...v,
        value: resolveTextValue(v.value, variables, values),
      })),
    }
  }
  if (p.type === 'symbol') {
    const ds = p.dataStructure
    if (ds.kind === 'raw' || ds.kind === 'gtin' || ds.kind === 'url') {
      return {
        ...p,
        dataStructure: { ...ds, value: resolveTextValue(ds.value, variables, values) },
      }
    }
    if (ds.kind === 'gs1-ai') {
      return {
        ...p,
        dataStructure: {
          ...ds,
          elements: ds.elements.map(el => ({
            ...el,
            value: resolveTextValue(el.value, variables, values),
          })),
        },
      }
    }
    return p
  }
  // image, separator: image-source field substitution is deferred to Option B;
  // until then dropped blocks render image fields as the placeholder, and the
  // user's props value lives only in BakedBlockInstance.props.
  return p
}

export function bakeBlock(
  block: BlockDefinition,
  values: PropValues,
  now: () => string = () => new Date().toISOString(),
  idGen: () => string = () => `bi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
): BakedBlockInstance {
  const variables = block.variables ?? {}
  const expansion = applyVariablesToLayout(block.layout, variables, values)
  return {
    type: 'baked-block-instance',
    id: idGen(),
    blockId: block.id,
    blockVersion: block.version,
    bakedAt: now(),
    bakedFrom: undefined,
    props: values,
    expansion,
    placement: {
      widthMm: block.widthMm,
      heightMm: block.heightMm,
      hAlign: 'center',
      vAlign: 'center',
    },
  }
}
